// MA-705 — `two_ten_frames_add` concept visual for TWO_TEN_FRAMES-AUTHORED
// ADDITION (MA-651 batch13 §3 C2, the ms.math.11 make-ten-standard addition
// twins of MA-655's `two_ten_frames_sub`). ms.math.11 is the one skill whose
// standard (1.OA.C.6) names the make-ten strategy by name; before this slice
// its 10 addition items (ME_11_002/004/006/008/010/012/014/016/018/020) fell
// to the generic `groups` arm and rendered as loose piles of up to 18 dots,
// losing the make-ten structure entirely.
//
// Renders two stacked ten frames (20 cells): cells 0..a-1 filled in colour A
// (the first addend), cells a..a+b-1 filled in colour B (the second addend,
// CONTINUING into frame 1's remaining empty cells and then into frame 2) —
// a linear fill across the 20-cell grid, so an item whose first addend alone
// exceeds 10 (e.g. ME_11_010: 12 + 5) still fills correctly: colour A spills
// from frame 1 into frame 2, and colour B picks up where it left off. This
// mirrors TwoTenFramesSubRenderer's own linear index math (`frame =
// Math.floor(i / 10)`), just filling forward instead of crossing out
// backward.
//
// D1 (operator decision, MA-705 spec): NO numerals, no labels, no total, no
// "10" callout inside the picture — the child composes the sum by counting
// the filled cells, nothing is printed for them to read off. The MA-647
// operand caption (`a + b`) is drawn above, same contract as
// TwoTenFramesSubRenderer (unconditional whenever mounted — the mapper only
// ever emits this kind with a real b>0, so "unconditional" and "b>0-gated"
// coincide here).
//
// ANSWER-SAFETY: by-design, same class as `groups`' two-group counted-all
// branch (MA-625 header: "two groups counted-all is right for a child still
// counting all") — the drawn cell COUNT (a+b) does equal the sum, exactly as
// counting two loose piles would, but nothing is LABELED with that number;
// the child produces it by counting, the same legitimate concrete model
// `groups` already is. Count-free aria-label (states structure, not a sum),
// matching TwoTenFramesSubRenderer's own precedent — MA-703's RED found an
// aria leak in a sibling slice; this label states the operands only, never
// their sum, exactly like TwoTenFramesSubRenderer's own aria never states
// the remainder.
//
// D2 (colours): reuses the SAME two operand marks every other two-operand
// renderer uses. MA-720 superseded the original fill-only precedent: the
// marks now come from ./operandPalette (orange solid for A; green WITH a 2px
// ink outline for B — a non-hue channel, because the two fills are ~1.03:1
// apart in luminance and adjacent cells here leave hue as the only cue).
//
// STAGED (mathVisualStage.ts STAGED_KINDS): fades to `count_on`/`equation`
// at bridging/abstract, mirroring `two_ten_frames_sub`'s own fade to
// `count_back`/`equation` — it models a compose-by-counting-on, not an
// authored make-ten frame the child must always read (that's `ten_frame`'s
// job, deliberately excluded from staging).
//
// ADDITIVE — registers itself under "two_ten_frames_add" (see
// rendererRegistry.tsx). The mapper (contentToEngineItem.ts, MA-705) emits
// this kind only when op==="+", the authored visualSupport/
// sourceStimulus_v2.type is "two_ten_frames", a>0, b>0, and a+b<=20 (two
// stacked ten-frames = 20 cells).
import type { MathRendererProps } from "./rendererRegistry";
import { OperandCaption, operandCaptionSize } from "./StagedMathRenderers";

// MA-720 — first/second operand treatment (B carries an ink outline as a
// non-hue channel) comes from the ONE shared module.
import { OPERAND_A_MARK, OPERAND_B_MARK, operandBOutlineAttr } from "./operandPalette";

const CELL = 68;
const PITCH = 70; // CELL + 2px gap
const COLS = 5;
const PAD = 6;
const FRAME_GAP = 16; // vertical gap between the two ten-frames
const R = 22;
const CAPTION_GAP = 14;

function TwoTenFramesAddRenderer({ a, b }: MathRendererProps) {
  const safeA = Math.max(0, Math.min(20, Math.floor(Number.isFinite(a ?? NaN) ? (a as number) : 0)));
  const safeB = Math.max(0, Math.min(20 - safeA, Math.floor(Number.isFinite(b ?? NaN) ? (b as number) : 0)));

  const gridW = COLS * PITCH + 2 * PAD; // 362
  const gridH = 4 * PITCH + 2 * PAD + FRAME_GAP;
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => {
    const frame = Math.floor(i / 10);
    const rowInFrame = Math.floor((i % 10) / COLS);
    return PAD + frame * (2 * PITCH + FRAME_GAP) + rowInFrame * PITCH;
  };

  const cap = operandCaptionSize();
  const svgW = Math.max(gridW, cap.width);
  const svgH = cap.height + CAPTION_GAP + gridH;
  const gridOffsetX = (svgW - gridW) / 2;
  const capOffsetX = (svgW - cap.width) / 2;
  const gridOffsetY = cap.height + CAPTION_GAP;
  // MA-728 — the mark operand B is drawn with (read by the hidden
  // data-operand-b-outline attribute below); it draws `safeB` marks.
  const markB = OPERAND_B_MARK;

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      width="100%"
      style={{ maxWidth: Math.min(360, svgW) }}
      role="img"
      aria-label="Two ten frames filling up with counters."
      // MA-728 — hidden capture hook, "true"/"false"/absent only.
      data-operand-b-outline={operandBOutlineAttr(markB, safeB)}
    >
      <OperandCaption a={safeA} b={safeB} op="+" x={capOffsetX} y={0} />
      <g transform={`translate(${gridOffsetX}, ${gridOffsetY})`}>
        {Array.from({ length: 20 }).map((_, i) => {
          const x = cellX(i);
          const y = cellY(i);
          const filledA = i < safeA;
          const filledB = i >= safeA && i < safeA + safeB;
          return (
            <g key={i}>
              <rect x={x} y={y} width={CELL} height={CELL} rx="8" fill="none"
                stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
              {filledA && (
                <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R} {...OPERAND_A_MARK} />
              )}
              {filledB && (
                <circle cx={x + CELL / 2} cy={y + CELL / 2} r={R} {...markB} />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default TwoTenFramesAddRenderer;
